import type { CustomEvent as ObsrviqCustomEvent } from '@logclip/types';
import type { InstrumentCtx, Teardown } from './context.js';
import { cssPath, pageMetrics, pagePoint } from './serialize.js';
import { uuid } from './util.js';

const RAGE_WINDOW_MS = 1000;
const RAGE_THRESHOLD = 3;
const DEAD_WAIT_MS = 3000;

function isInteractive(el: Element | null): boolean {
  if (!el) return false;
  let node: Element | null = el;
  for (let i = 0; i < 4 && node; i++) {
    const tag = node.tagName.toLowerCase();
    if (['a', 'button', 'input', 'select', 'textarea', 'label', 'summary'].includes(tag))
      return true;
    if (node.getAttribute('role') === 'button' || node.hasAttribute('onclick')) return true;
    if (node.getAttribute('contenteditable') === 'true') return true;
    node = node.parentElement;
  }
  return false;
}

export function instrumentInteractions(ctx: InstrumentCtx): Teardown {
  let recent: { el: Element; t: number }[] = [];
  let lastRageAt = -Infinity;

  const emitCustom = (name: string, props: Record<string, unknown>, t: number) => {
    const e: ObsrviqCustomEvent = {
      id: uuid(),
      sessionId: ctx.sessionId,
      type: 'custom',
      t,
      ts: ctx.clock.wall(),
      name,
      props,
    };
    ctx.emit(e);
  };

  const onClick = (ev: MouseEvent) => {
    const target = ev.target as Element | null;
    if (!target || !(target instanceof Element)) return;
    const t = ctx.clock.now();
    const selector = cssPath(target);

    // ── Click heatmap: one event per click, anchored to the exact element ──
    // Stored against (path, selector, device) and rolled up server-side. rx/ry are
    // the click position WITHIN the element (0..1) so heat re-projects at any size.
    {
      const rect = target.getBoundingClientRect();
      const rx = rect.width > 0 ? Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width)) : 0.5;
      const ry = rect.height > 0 ? Math.min(1, Math.max(0, (ev.clientY - rect.top) / rect.height)) : 0.5;
      const label = (target.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 40);
      // Page-space coordinates as well as the element anchor. The element is the
      // better anchor when it resolves — it re-projects correctly at any width —
      // but a selector can fail (the element is gone, or the page rebuilt
      // differently), and without coordinates such a click is unplaceable and
      // gets silently dropped. px/py are normalised 0..1 against the full page
      // so they survive any later viewport.
      const pm = pageMetrics();
      const pt = pagePoint(target, ev.clientX, ev.clientY);
      emitCustom(
        '_click',
        {
          selector,
          path: location.pathname,
          vw: window.innerWidth,
          vh: window.innerHeight,
          rx: Math.round(rx * 1000) / 1000,
          ry: Math.round(ry * 1000) / 1000,
          px: Math.round(Math.min(1, Math.max(0, pt.x / pm.w)) * 10000) / 10000,
          py: Math.round(Math.min(1, Math.max(0, pt.y / pm.h)) * 10000) / 10000,
          pw: pm.w,
          ph: pm.h,
          tag: target.tagName.toLowerCase(),
          label,
        },
        t,
      );
    }

    // ── Rage click detection ──
    recent = recent.filter((r) => t - r.t <= RAGE_WINDOW_MS);
    recent.push({ el: target, t });
    const sameTarget = recent.filter((r) => r.el === target);
    if (sameTarget.length >= RAGE_THRESHOLD && t - lastRageAt > RAGE_WINDOW_MS) {
      lastRageAt = t;
      emitCustom('rage_click', { selector, count: sameTarget.length }, t);
    }

    // ── Dead click detection ──
    if (!isInteractive(target)) {
      const before = ctx.lastActivityT();
      window.setTimeout(() => {
        // No app activity (mutation/nav/network) after the click → dead.
        if (ctx.lastActivityT() <= Math.max(before, t)) {
          emitCustom('dead_click', { selector }, t);
        }
      }, DEAD_WAIT_MS);
    }
  };

  window.addEventListener('click', onClick, true);
  return () => window.removeEventListener('click', onClick, true);
}
